import SearchArea from 'component/admin/search/searchArea';
import style from 'css/admin/component.module.css';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Input, Popover, Table, Tag, message } from "antd";
import { ReactComponent as PlusIcon } from "asset/plus_icon.svg";
import axios from 'axios';
import PeriodDeleteConfirmModal from "component/admin/modal/periodDeleteConfirmModal";
import PeriodModal from 'component/admin/modal/periodModal';
import SearchButton from "component/admin/search/searchButton";
import SearchDatePicker from "component/admin/search/searchDatePicker";
import SearchInput from "component/admin/search/searchInput";
import SearchItem from "component/admin/search/searchItem";
import dayjs from "dayjs";

function PeriodLicenseCell({ licenseList, getItems, loading = false }) {
    const [query, setQuery] = useState("");

    const items = useMemo(() => getItems(licenseList), [getItems, licenseList]);
    const filtered = useMemo(() => {
        const q = String(query || "").trim().toLowerCase();
        if (!q) return items;
        return items.filter((v) => String(v?.label ?? "").toLowerCase().includes(q));
    }, [items, query]);

    const popoverTitle = (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <span style={{ fontWeight: 600 }}>신청 가능 제품 ({items.length}개)</span>
            <Input
                size="small"
                placeholder="검색"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                allowClear
                style={{ width: 180 }}
            />
        </div>
    );

    const popoverContent = (
        <div style={{ width: 360, maxHeight: 260, overflow: "auto", paddingRight: 6 }}>
            {filtered.length === 0 ? (
                <div style={{ color: "#6b7280" }}>검색 결과가 없습니다.</div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {filtered.map((it) => (
                        <div
                            key={it.key}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                padding: "6px 8px",
                                border: "1px solid #eef0f2",
                                borderRadius: 10,
                                background: "#fff",
                            }}
                        >
                            <img
                                src={it.src}
                                alt={it.label}
                                loading="lazy"
                                style={{ width: 18, height: 18, objectFit: "contain", flex: "0 0 auto" }}
                                onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = it.defaultSrc;
                                }}
                            />
                            <div style={{ fontSize: 13, lineHeight: 1.2, wordBreak: "break-word" }}>{it.label}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    if (items.length === 0) return <span>-</span>;

    return (
        <Popover
            title={popoverTitle}
            content={popoverContent}
            trigger="click"
            placement="topLeft"
            overlayStyle={{ maxWidth: 420 }}
            onOpenChange={(open) => {
                if (!open) setQuery("");
            }}
        >
            <Button
                size="small"
                style={{
                    opacity: loading ? 0.7 : 1,
                    borderRadius: 999,
                    height: 28,
                    paddingInline: 12,
                }}
                title={items.map((v) => v.label).join(", ")}
            >
                제품 {items.length}개
            </Button>
        </Popover>
    );
}

function System() {
    const [init, setInit] = useState(true);

    // 검색
    const [searchStartDate, setStartDate] = useState(dayjs());
    const [searchEndDate, setEndDate] = useState(dayjs().add(7, "day"));
    const [searchExpireDate, setExpireDate] = useState(dayjs());
    const [searchText, setSearchText] = useState('');

    const [isLoading, setIsLoading] = useState(false);
    const [dataList, setDataList] = useState([]);
    const [licenseIndex, setLicenseIndex] = useState(() => new Map());
    const [licenseIndexLoading, setLicenseIndexLoading] = useState(false);
    const [campusMap, setCampusMap] = useState(() => new Map());
    const [registerModalOpen, setRegisterModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("create"); // create | edit
    const [selectedRecord, setSelectedRecord] = useState(null);

    // 삭제 확인 모달
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const getPeriodList = useCallback(() => {
        setIsLoading(true);
        const toDate = (d) => (d ? dayjs(d).format('YYYY-MM-DD') : undefined);
        const params = {
            PERIOD_NM: searchText || undefined,
            START_DT: toDate(searchStartDate),
            END_DT: toDate(searchEndDate),
            EXPIRE_DT: toDate(searchExpireDate),
        };

        axios
            .get("/period", { params })
            .then((res) => {
                if (res.status === 200 && Array.isArray(res.data)) {
                    setDataList(res.data);
                } else {
                    setDataList([]);
                }
            })
            .catch((err) => {
                console.log(err);
                setDataList([]);
            })
            .finally(() => setIsLoading(false));
    }, [searchText, searchStartDate, searchEndDate, searchExpireDate]);

    const getLicenseIndex = useCallback(() => {
        // 이미 로드된 상태면 재호출하지 않음
        if (licenseIndex && licenseIndex.size > 0) return;
        setLicenseIndexLoading(true);
        axios
            .get("/product/detail")
            .then((res) => {
                if (res.status === 200 && Array.isArray(res.data)) {
                    const m = new Map();
                    for (const row of res.data) {
                        const manuCd = row?.MANUFACTURER_CD;
                        const prodCd = row?.PRODUCT_CD;
                        if (!manuCd || !prodCd) continue;
                        const manuNm = row?.MANUFACTURER_NM ?? manuCd;
                        const prodNm = row?.PRODUCT_NM ?? prodCd;
                        m.set(`${manuCd}|${prodCd}`, {
                            MANUFACTURER_CD: manuCd,
                            PRODUCT_CD: prodCd,
                            MANUFACTURER_NM: manuNm,
                            PRODUCT_NM: prodNm,
                            IMG_NM: row?.IMG_NM ?? null,
                        });
                    }
                    setLicenseIndex(m);
                } else {
                    setLicenseIndex(new Map());
                }
            })
            .catch((err) => {
                console.log(err);
                setLicenseIndex(new Map());
            })
            .finally(() => setLicenseIndexLoading(false));
    }, [licenseIndex]);

    const getCampusMap = useCallback(() => {
        if (campusMap && campusMap.size > 0) return;
        const config = { params: { GROUP_CD: 'CD06' } };
        axios.get("/code", config)
            .then((res) => {
                if (res.status === 200 && Array.isArray(res.data)) {
                    const m = new Map();
                    for (const row of res.data) {
                        m.set(row?.CODE_CD, row?.VIEW_NM);
                    }
                    setCampusMap(m);
                }
            })
            .catch((err) => console.log(err));
    }, [campusMap]);

    const getPeriodLicenseItems = useCallback((licenseList) => {
        const list = Array.isArray(licenseList) ? licenseList : [];
        if (list.length === 0) return [];

        return list
            .map((v) => {
                const manuCd = v?.MANUFACTURER_CD;
                const prodCd = v?.PRODUCT_CD;
                if (!manuCd || !prodCd) return null;
                const key = `${manuCd}|${prodCd}`;
                const row = licenseIndex?.get?.(key);
                if (row) {
                    const manuNm = row?.MANUFACTURER_NM ?? manuCd;
                    const prodNm = row?.PRODUCT_NM ?? prodCd;
                    const imgNm = row?.IMG_NM ? String(row.IMG_NM) : "";
                    const manuPath = manuNm ? String(manuNm).toLowerCase() : "";
                    const defaultSrc = `${process.env.REACT_APP_CLIENT_URL}icon/logo.jpg`;
                    const src = imgNm && manuPath
                        ? `${process.env.REACT_APP_CLIENT_URL}img/product/${manuPath}/${imgNm}`
                        : defaultSrc;
                    return {
                        key,
                        label: `${manuNm} ${prodNm}`,
                        src,
                        defaultSrc,
                    };
                }
                return {
                    key,
                    label: `${manuCd} ${prodCd}`,
                    src: `${process.env.REACT_APP_CLIENT_URL}icon/logo.jpg`,
                    defaultSrc: `${process.env.REACT_APP_CLIENT_URL}icon/logo.jpg`,
                };
            })
            .filter(Boolean);
    }, [licenseIndex]);

    useEffect(() => {
        if (init) {
            getPeriodList();
            getLicenseIndex();
            getCampusMap();
            setInit(false);
        }
    }, [getPeriodList, getLicenseIndex, getCampusMap, init])

    const openCreateModal = useCallback(() => {
        setModalMode("create");
        setSelectedRecord(null);
        setRegisterModalOpen(true);
    }, []);

    const openEditModal = useCallback((record) => {
        setModalMode("edit");
        setSelectedRecord(record);
        setRegisterModalOpen(true);
    }, []);

    const deleteHandler = useCallback((record) => {
        const periodId = record?.PERIOD_ID;
        if (!periodId) return;
        setDeleteTarget(record || null);
        setDeleteModalOpen(true);
    }, []);

    const closeDeleteModal = useCallback(() => {
        if (deleteLoading) return;
        setDeleteModalOpen(false);
        setDeleteTarget(null);
    }, [deleteLoading]);

    const submitDelete = useCallback(async () => {
        const periodId = deleteTarget?.PERIOD_ID;
        if (!periodId) return;

        try {
            setDeleteLoading(true);
            const res = await axios.delete(`/period/${periodId}`);
            if (res.status === 200) {
                message.success("삭제되었습니다.");
                closeDeleteModal();
                getPeriodList();
            } else {
                message.error("삭제에 실패했습니다.");
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.log(err);
            message.error("삭제 중 오류가 발생했습니다.");
        } finally {
            setDeleteLoading(false);
        }
    }, [closeDeleteModal, deleteTarget, getPeriodList]);

    const columns = useMemo(() => ([
        {
            title: '제목',
            dataIndex: 'PERIOD_NM',
            key: 'PERIOD_NM',
            width: 220,
            ellipsis: true,
        },
        {
            title: '캠퍼스',
            dataIndex: 'CAMPUS_CD',
            key: 'CAMPUS_CD',
            width: 110,
            align: "center",
            render: (campusCd) => {
                if (!campusCd) return <Tag>전체</Tag>;
                const name = campusMap?.get?.(campusCd);
                return name ? <Tag>{name}</Tag> : <Tag>전체</Tag>;
            },
        },
        {
            title: '신청 시작일',
            dataIndex: 'START_DT',
            key: 'START_DT',
            width: 155,
            align: "center",
        },
        {
            title: '신청 종료일',
            dataIndex: 'END_DT',
            key: 'END_DT',
            width: 155,
            align: "center",
        },
        {
            title: '라이센스 시작일',
            dataIndex: 'ACTIVE_DT',
            key: 'ACTIVE_DT',
            width: 130,
            align: "center",
        },
        {
            title: '라이센스 만료일',
            dataIndex: 'EXPIRE_DT',
            key: 'EXPIRE_DT',
            width: 130,
            align: "center",
        },
        {
            title: '신청 가능 제품',
            dataIndex: 'LICENSE_LIST',
            key: 'LICENSE_LIST',
            width: 130,
            align: "center",
            render: (licenseList) => {
                return (
                    <PeriodLicenseCell
                        licenseList={licenseList}
                        getItems={getPeriodLicenseItems}
                        loading={licenseIndexLoading}
                    />
                )
            },
        },
        { title: '등록일시', dataIndex: 'INPUT_DT', key: 'INPUT_DT', width: 150, align: "center" },
        {
            key: 'actions',
            fixed: 'right',
            width: 100,
            render: (_, record) => {
                return (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 6 }}>
                        <Button
                            className={style.edit_btn}
                            type="text"
                            icon={<EditOutlined />}
                            aria-label="수정"
                            title="수정"
                            onClick={() => openEditModal(record)}
                        />
                        <Button
                            className={style.delete_btn}
                            type="default"
                            icon={<DeleteOutlined />}
                            aria-label="삭제"
                            title="삭제"
                            onClick={() => deleteHandler(record)}
                        />
                    </div>
                );
            },
        },
    ]), [openEditModal, deleteHandler, getPeriodLicenseItems, licenseIndexLoading, campusMap]);

    return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 20 }}>
            <SearchArea title="신청 기간 관리">
                <div className={style.searchItemArea}>
                    <SearchItem label="제목">
                        <SearchInput
                            placeholder="예) 2025년 1학기" width={230}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') getPeriodList();
                            }}
                        />
                    </SearchItem>
                    <SearchItem label="신청 시작일">
                        <SearchDatePicker
                            value={searchStartDate}
                            onChange={setStartDate}
                            width={230}
                        />
                    </SearchItem>
                    <SearchItem label="신청 종료일">
                        <SearchDatePicker
                            value={searchEndDate}
                            onChange={setEndDate}
                            width={230}
                        />
                    </SearchItem>
                    <SearchItem label="라이센스 만료일">
                        <SearchDatePicker
                            value={searchExpireDate}
                            onChange={setExpireDate}
                            width={230}
                        />
                    </SearchItem>


                    <SearchItem>
                        <SearchButton>조회</SearchButton>
                    </SearchItem>
                </div>
            </SearchArea>

            <div className={style.content_wrapper}>
                <div className={style.content_container}>
                    <div className={style.header}>
                        <div className={style.text}>전체 신청 기간 (총 {dataList.length}건)</div>
                        <div className={style.buttons}>
                            <Button className={style.register_btn} onClick={openCreateModal}>
                                <PlusIcon width={20} height={20} />
                                신청 기간
                            </Button>
                        </div>
                    </div>
                    <div className={style.content}>
                        <div style={{ marginTop: 16 }}>
                            <Table
                                columns={columns}
                                dataSource={dataList}
                                size="middle"
                                pagination={{ pageSize: 10, showSizeChanger: false }}
                                rowKey="PERIOD_ID"
                                scroll={{ x: 1150 }}
                                loading={isLoading}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <PeriodModal
                isOpen={registerModalOpen}
                close={() => setRegisterModalOpen(false)}
                mode={modalMode}
                initialData={selectedRecord}
                onSuccess={getPeriodList}
            />

            <PeriodDeleteConfirmModal
                open={deleteModalOpen}
                onCancel={closeDeleteModal}
                onConfirm={submitDelete}
                loading={deleteLoading}
                record={deleteTarget}
            />
        </div>
    )
}

export default System
